import QRCode from 'qrcode';
import { useId, useMemo } from 'react';

import DarkLogo from '../../assets/icons/qr-logo-dark.svg?react';
import LightLogo from '../../assets/icons/qr-logo-light.svg?react';
import { useUiClientConfig } from '../../config-context';

interface RoundedQrCodeProps {
  value: string;
  title: string;
  size?: number;
}

/** Theme-aware QR modules with real SVG curves, without raster filters. */
export function RoundedQrCode({
  value,
  title,
  size = 192,
}: RoundedQrCodeProps) {
  const { themeMode } = useUiClientConfig();
  const logoMaskId = useId();
  const Logo = themeMode === 'dark' ? DarkLogo : LightLogo;
  const modules = useMemo(
    () => QRCode.create(value, { errorCorrectionLevel: 'H' }).modules,
    [value],
  );
  const margin = 1;
  const extent = modules.size + margin * 2;
  const logoSize = modules.size * 0.22;
  // Trim precisely around the logo instead of removing every intersecting cell.
  const clearRadius = logoSize / 2 + 0.6;
  const finders = [
    [0, 0],
    [modules.size - 7, 0],
    [0, modules.size - 7],
  ];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${extent} ${extent}`}
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <defs>
        <mask
          id={logoMaskId}
          maskUnits="userSpaceOnUse"
          x={0}
          y={0}
          width={extent}
          height={extent}
          style={{ maskType: 'luminance' }}
        >
          <rect width={extent} height={extent} fill="white" />
          <circle
            cx={extent / 2}
            cy={extent / 2}
            r={clearRadius}
            fill="black"
          />
        </mask>
      </defs>
      <g fill="var(--app-color-text-primary)" mask={`url(#${logoMaskId})`}>
        {Array.from(modules.data, (dark, index) => {
          const x = index % modules.size;
          const y = Math.floor(index / modules.size);
          if (
            !dark ||
            finders.some(
              ([fx, fy]) => x >= fx && x < fx + 7 && y >= fy && y < fy + 7,
            )
          ) {
            return null;
          }
          return (
            <rect
              key={index}
              x={x + margin}
              y={y + margin}
              width={1}
              height={1}
              rx={0.3}
            />
          );
        })}
        {finders.map(([x, y]) => (
          <g
            key={`${x},${y}`}
            transform={`translate(${x + margin} ${y + margin})`}
          >
            {/* Transparent rings keep the existing card background visible. */}
            <rect
              x={0.5}
              y={0.5}
              width={6}
              height={6}
              rx={1.5}
              fill="none"
              stroke="var(--app-color-text-primary)"
              strokeWidth={1}
            />
            <rect x={2} y={2} width={3} height={3} rx={0.65} />
          </g>
        ))}
      </g>
      <svg
        x={(extent - logoSize) / 2}
        y={(extent - logoSize) / 2}
        width={logoSize}
        height={logoSize}
        viewBox="0 0 60 60"
        aria-hidden="true"
      >
        <Logo />
      </svg>
    </svg>
  );
}
